<template>
  <section class="about-hero" id="identification">
    <!-- franja huesos (svg generado por código) -->
    <div class="about-hero__bones" aria-hidden="true">
      <i v-for="n in 26" :key="`bone-${n}`" class="fi fi-sr-bone about-hero__bone-icon"></i>
    </div>

    <div class="about-hero__inner">
      <!-- LEFT -->
      <div class="about-hero__media">
        <img :src="imageSrc" alt="Mascota" class="about-hero__img" />
      </div>

      <!-- RIGHT -->
      <div class="about-hero__copy">
        <h1 class="about-hero__title">
          TECNOLOGÍA<br />
          INTELIGENTE<br />
          QUE <span class="strong">PROTEGER</span><br />
          <span class="strong">A TU MASCOTA</span>
        </h1>

        <p class="about-hero__text">
          Somos una marca enfocada en prevención y cuidado responsable.
          Desarrollamos una identificación inteligente que integra tecnología NFC,
          código QR y sistema GPS para centralizar información médica, datos de
          contacto y ubicación de tu mascota en un solo perfil accesible desde
          cualquier smartphone. <br />
          No es solo una placa. Es una herramienta de seguridad real.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "AboutHero",
  props: {
    imageSrc: { type: String, default: require("@/assets/IMG/Perro.png") },
  },
};
</script>

<style scoped>
/* =========================
   SECCIÓN
========================= */
.about-hero {
  position: relative;
  width: 100%;
  margin: 0;

  padding: clamp(24px, 3.1vw, 38px) 0 clamp(68px, 8.5vw, 118px);
  overflow: hidden;

  background: #efefe4;

  scroll-margin-top: 89px;

  /* ===== variables para huesos ===== */
  --bones-top: 80%;          /* posición vertical (sube/baja) */
  --bones-band-height: 170px;/* alto de la franja */
  --bones-tile-w: 120px;     /* tamaño del patrón (ancho) */
  --bones-tile-h: 60px;      /* tamaño del patrón (alto) */
  --bones-opacity: 0.55;     /* opacidad del patrón */
  --bones-color: #9bb9df;    /* color del hueso */
}

/* Huella arriba derecha */
.about-hero::before {
  content: "";
  position: absolute;
  top: clamp(2px, 2vw, 28px);
  right: clamp(10px, 2.2vw, 34px);
  width: clamp(108px, 14vw, 210px);
  height: clamp(108px, 14vw, 210px);
  background-image: url("@/assets/IMG/huella.png");
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  pointer-events: none;
  z-index: 1;
}

/* =========================
   HUESOS: SVG hecho por código (SIN distorsión)
========================= */
.about-hero__bones {
  position: absolute;
  left: 0;
  right: 0;
  top: var(--bones-top);
  height: var(--bones-band-height);
  display: flex;
  align-items: center;
  gap: clamp(14px, 1.5vw, 24px);
  white-space: nowrap;
  overflow: hidden;
  opacity: var(--bones-opacity);
  pointer-events: none;
  z-index: 1;
}

.about-hero__bone-icon {
  color: var(--bones-color);
  font-size: clamp(66px, 6vw, 92px);
  line-height: 1;
  flex: 0 0 auto;
  display: inline-block;
  transform: rotate(45deg) scaleX(1);
  transform-origin: center;
}

/* =========================
   CONTENIDO
========================= */
.about-hero__inner {
  width: 100%;
  margin: 0;

  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: clamp(24px, 4vw, 64px);
  padding-right: clamp(14px, 3.2vw, 48px);
  position: relative;
  z-index: 2; /* por encima de huesos */
}

/* IZQUIERDA */
.about-hero__media {
  position: relative;
  min-height: clamp(480px, 49vw, 700px);
  overflow: visible;
}

.about-hero__img {
  position: absolute;
  left: 0;
  bottom: 0%;
  width: auto;
  height: 98%;
  max-width: 100%;
  object-fit: contain;
  object-position: left bottom;
  z-index: 2;

  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.25));
  pointer-events: none;
  user-select: none;
}

/* DERECHA */
.about-hero__copy {
  position: relative;
  color: #1e5fae;
  text-align: left;
  padding-right: clamp(0px, 1vw, 10px);
  transform: translateY(-28px);
}

.about-hero__title {
  margin: 0;
  font-family: "Barlow", sans-serif;
  text-transform: uppercase;
  line-height: 0.98;
  font-weight: 300;
  letter-spacing: 0.02em;
  font-size: clamp(44px, 5.8vw, 84px);
}

.about-hero__title .strong {
  font-weight: 550;
}

.about-hero__text {
  margin: clamp(14px, 2vw, 22px) 0 0;
  font-family: "Barlow", sans-serif;
  font-weight: 400;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.72);
  font-size: clamp(17px, 1.55vw, 20px);
  max-width: 560px;
}

/* =========================
   RESPONSIVE
========================= */
@media (max-width: 980px) {
  .about-hero {
    padding: 26px 0 92px;
    --bones-top: 82%;
    --bones-band-height: 146px;
    --bones-tile-w: 112px;
    --bones-tile-h: 56px;
  }

  .about-hero__inner {
    grid-template-columns: 1fr;
    gap: 18px;
    width: min(900px, 92%);
    margin: 0 auto;
    padding-right: 0;
  }

  .about-hero__copy {
    order: 1;
    transform: translateY(0);
    text-align: center;
    padding-right: 0;
  }

  .about-hero__title {
    font-size: clamp(40px, 7.2vw, 64px);
    line-height: 1;
  }

  .about-hero__text {
    font-size: clamp(16px, 2.2vw, 18px);
    line-height: 1.52;
    max-width: 100%;
  }

  .about-hero__media {
    order: 2;
    min-height: 430px;
  }

  .about-hero__img {
    bottom: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: min(560px, 92%);
    height: auto;
    max-width: none;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .about-hero__inner {
    grid-template-columns: 1.02fr 0.98fr;
    gap: clamp(18px, 2.6vw, 34px);
    width: min(980px, 94%);
    margin: 0 auto;
    padding-right: 0;
  }

  .about-hero__media {
    order: 1;
    min-height: 400px;
  }

  .about-hero__copy {
    order: 2;
    text-align: left;
    transform: translateY(0);
    padding-right: 0;
  }

  .about-hero__text {
    max-width: 100%;
  }

  .about-hero__media {
    min-height: 400px;
  }

  .about-hero__img {
    left: 0;
    bottom: -1%;
    width: 100%;
    transform: none;
  }
}

@media (max-width: 560px) {
  .about-hero {
    padding: 16px 0 66px;
    min-height: 860px;

    --bones-top: 85%;
    --bones-band-height: 112px;
    --bones-tile-w: 96px;
    --bones-tile-h: 48px;
    --bones-opacity: 0.5;
  }

  .about-hero::before {
    width: 82px;
    height: 82px;
    right: 8px;
    top: 10px;
  }

  .about-hero__copy {
    transform: translateY(0);
  }

  .about-hero__media {
    min-height: 320px;
  }

  .about-hero__img {
    bottom: -12%;
    left: 50%;
    width: min(420px, 102%);
    transform: translateX(-50%);
    height: auto;
  }

  .about-hero__title {
    font-size: clamp(32px, 9vw, 44px);
    line-height: 1;
  }

  .about-hero__text {
    font-size: 15px;
    line-height: 1.5;
    max-width: 100%;
  }
}

@media (max-width: 390px) {
  .about-hero {
    min-height: 900px;
  }
}
</style>